import './ProductActionsV2.scss';

import React, { useCallback, useEffect, useReducer, useState } from 'react';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import InlineMessage from '@ulta/core/components/InlineMessage/InlineMessage';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { useUserContext } from '@ulta/core/providers/UserContextProvider/UserContextProvider';
import { hasItems } from '@ulta/core/utils/array/array';
import datacapture from '@ulta/core/utils/datacapture/datacapture';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import Tag from '@ulta/modules/Tag/Tag';

import FulfillmentButton from '@ulta/components/FulfillmentButton/FulfillmentButton';
import NotifyMeV2 from '@ulta/components/NotifyMeV2/NotifyMeV2';
import PdpSelectableCard from '@ulta/components/PdpSelectableCard/PdpSelectableCard';
import ProductComingSoon from '@ulta/components/ProductComingSoon/ProductComingSoon';
import ProductPartnerInfo from '@ulta/components/ProductPartnerInfo/ProductPartnerInfo';
import ProductPreSale from '@ulta/components/ProductPresale/ProductPresale';
import StickyContainer from '@ulta/components/StickyContainer/StickyContainer';
import Subscription from '@ulta/components/Subscription/Subscription';
import TileDeliveryInfo from '@ulta/components/TileDeliveryInfo/TileDeliveryInfo';

import constants from '@ulta/utils/constants/constants';

import * as utils from './ProductActionsV2';

export const secondaryEligibilityStates = ['change_store', 'change_address'];
export const disabledEligibilityStates = ['out_of_stock'];

export const ProductActionsV2 = ( props ) => {
  const {
    bookmarkAction,
    bookmarked,
    comingSoon,
    isGWP,
    componentKey,
    displayState,
    fulfillmentSection = {},
    invokeAction,
    invokeMutation,
    messages,
    notifyMe,
    perk,
    productId,
    partnerInfo,
    removeBookmarkAction,
    skuId,
    viewItemsInOfferAction,
    updateSuccessLabel,
    removeSubscriptionMessage,
    inStore,
    isWebStickyATBButton,
    stickyAddToBagAccessibilityLabel
  } = props;


  const { cart, store, sddStore, sddInfo } = useUserContext();
  const [isFulfillmentChanged, setIsFulfillmentChanged] = useState( false );
  useEffect( () => {
    utils.onModuleUpdate(
      { fulfillmentSection, props, cart, store, sddStore, sddInfo, isFulfillmentChanged },
      { setIsSubscribed, setSubscriptionFrequency, setShowUpdateBag, setPrevLabel, setOriginalModuleState, dispatch }
    );
  }, [fulfillmentSection, perk, cart, store, sddStore, sddInfo] );
  const { breakpoint } = useDeviceInflection();
  const isLargeDevice = breakpoint?.isLargeDevice();
  const [state, dispatch] = useReducer( utils.fulfillmentStateReducer, props );
  const [showUpdateBag, setShowUpdateBag] = useState( false );
  const [isSubscribed, setIsSubscribed] = useState( false );
  const [originalModuleState, setOriginalModuleState] = useState( {} );

  // User's selected fulfillment ( or default from DXL )
  const selectedFulfillment  = utils.getSelectedFulfillment( state.fulfillmentSection?.fulfillmentTileOptions ) || {};
  const { eligibilityState, deliveryInfo, subscription } = selectedFulfillment;

  // User's selected subscription and frequency
  const [subscriptionFrequency, setSubscriptionFrequency] = useState( subscription?.frequencyOptions?.value );
  const [prevLabel, setPrevLabel] = useState( selectedFulfillment.buttonAction?.label );

  const isSecondary = utils.isSecondaryState( { eligibilityState } );
  const isDisabled = utils.isDisabledState( { eligibilityState } );

  const components = getRenderComponents( {
    comingSoon, displayState, inStore, notifyMe, PDP_ACTIONS_DISPLAY_STATE,
    messages, perk, removeSubscriptionMessage, selectedFulfillment, showUpdateBag, deliveryInfo
  },
  { hasItems }
  );

  const subscriptionChangeHandler = useCallback(
    utils.subscriptionChangeHandler(
      { prevLabel, selectedFulfillment, originalModuleState, subscriptionFrequency },
      { setIsSubscribed, setSubscriptionFrequency, setShowUpdateBag, dispatch } ),
    [selectedFulfillment, prevLabel, originalModuleState]
  );

  const frequencyChangeHandler = useCallback(
    utils.frequencyChangeHandler(
      { selectedFulfillment, originalModuleState },
      { setSubscriptionFrequency, setShowUpdateBag, dispatch } ),
    [selectedFulfillment, originalModuleState]
  );

  const fulfillmentChangeHandler = useCallback(
    utils.fulfillmentChangeHandler( { skuId, productId, isSubscribed, subscriptionFrequency, originalModuleState, fulfillmentOptions: state.fulfillmentSection?.fulfillmentTileOptions, cart, store, sddStore, sddInfo }, { dispatch, invokeAction, datacapture, setShowUpdateBag, setIsFulfillmentChanged } ),
    [skuId, productId, isSubscribed, subscriptionFrequency, originalModuleState, cart, store, sddStore, sddInfo]
  );

  const renderInlineMessages = () => {
    if( components.inlineMessages ){
      return components.inlineMessages.map( ( messageItem, index ) => (
        <div key={ `${index}-${componentKey}` }
          className='ProductActionsV2--info-message'
        >
          <InlineMessage
            message={ messageItem.message }
            messageType={ messageItem.icon }
            { ...( messageItem.icon === 'error' && { role: 'alert' } ) }
          />
        </div>
      ) );
    }
    return null;
  };

  const stickyContent =
  (
    <>
      { components.addToBag &&
        <AddToBag
          { ...spreadProps( { ...state } ) }
          isWebStickyATBButton={ isWebStickyATBButton }
          stickyAddToBagAccessibilityLabel={ stickyAddToBagAccessibilityLabel }
          selectedFulfillment={ selectedFulfillment }
          isSecondary={ isSecondary }
          isDisabled={ isDisabled }
          isSubscribed={ isSubscribed }
          subscriptionFrequency={ subscriptionFrequency }
          invokeAction={ invokeAction }
          updateSuccessLabel={ updateSuccessLabel }
          showUpdateBag={ showUpdateBag }
          setIsFulfillmentChanged={ setIsFulfillmentChanged }
        />
      }
      { renderInlineMessages() }
      { components.subscriptionMessages &&
        <InlineMessage
          data-testid='inline-message'
          role='status'
          message={ removeSubscriptionMessage.message }
          messageType={ removeSubscriptionMessage.icon }
        />
      }
      { viewItemsInOfferAction &&
        <ViewItemsInOffer
          { ...spreadProps( { ...state } ) }
          viewItemsInOfferAction={ viewItemsInOfferAction }
        />
      }
    </>
  );
  const addToBagContent = renderWithStickyContainer( { isLargeDevice, isWebStickyATBButton, component: stickyContent } );

  return (
    <div className='ProductActionsV2'>
      <div className='FulfillmentSection'>
        { state.fulfillmentSection &&
        <>
          <legend className='FulfillmentSection__heading'>
            <Text
              htmlTag={ state.fulfillmentSection.title?.htmlTag }
              textStyle='title'
            >
              { state.fulfillmentSection.title.text }
            </Text>
          </legend>
          { components.fulfillmentTiles &&
            <ul className='FulfillmentSection__tiles'>
              <utils.FulfillmentTiles
                { ...spreadProps( { ...state } ) }
                onChange={ fulfillmentChangeHandler }
              />
            </ul>
          }
        </>
        }
        { deliveryInfo &&
          <TileDeliveryInfo
            { ...spreadProps( { ...deliveryInfo } ) }
            invokeAction={ invokeAction }
            productId={ productId }
            skuId={ skuId }
          />
        }
        { components.showAlternateStoreMessage &&
          <div className='ProductActionsV2__alternateStoreTag'>
            <Tag
              variant='utility'
              style='validation'
              message={ deliveryInfo?.alternateStoreTag?.message }
            />
          </div>
        }
        { !!selectedFulfillment.subscription &&
          <div className='ProductActionsV2__Susbcription'>
            <Subscription
              { ...spreadProps( { ...selectedFulfillment.subscription } ) }
              onSubscriptionChange={ subscriptionChangeHandler }
              onSubscriptionFrequencyChange={ frequencyChangeHandler }
              invokeAction={ invokeAction }
            />
          </div>
        }
        { partnerInfo &&
          <ProductPartnerInfo { ...partnerInfo } />
        }
        <div className='ProductActionsV2--bottom-spacer'>
          { addToBagContent }
          { components.perk &&
            <utils.PlatinumPerk
              { ...spreadProps( { ...state } ) }
            /> }
          { components.comingSoon &&
            <utils.ComingSoon
              comingSoonLabel={ comingSoon.comingSoonLabel }
              comingSoonMessage={ comingSoon.comingSoonMessage }
              bookmarked={ bookmarked }
              bookmarkAction={ bookmarkAction }
              removeBookmarkAction={ removeBookmarkAction }
              invokeMutation={ invokeMutation }
              productId={ productId }
              skuId={ skuId }
            /> }
          { components.notifyMe &&
            <NotifyMeV2
              email={ notifyMe.email }
              notifyMeMessage={ notifyMe.notifyMeMessage }
              notifyMeAction={ notifyMe.notifyMeAction }
              notifyMeDisabled={ notifyMe.notifyMeDisabled }
              invokeMutation={ invokeMutation }
              { ...spreadProps( { ...props } ) }
            /> }
          { components.inStore &&
            <utils.InStoreOnlyItem
              message={ inStore.message }
              bookmarkAction={ bookmarkAction }
              removeBookmarkAction={ removeBookmarkAction }
              invokeMutation={ invokeMutation }
              productId={ productId }
              skuId={ skuId }
              { ...props }
            />
          }
        </div>
      </div>
    </div>
  );
};

/**
 * Function to return FulfillmentTiles.
 *
 * @method FulfillmentTiles
 * @param {object} fulfillmentSection - fulfillmentSection.
 * @param {function} onChange - onChange function
 * @returns {JSX.Element|null} A list of `PdpSelectableCard`
 **/
export const FulfillmentTiles = ( props ) => {
  const {
    fulfillmentSection,
    onChange
  } = props || {};

  // Early return
  if( !fulfillmentSection ){
    return null;
  }

  return (
    fulfillmentSection.fulfillmentTileOptions?.map( ( tile ) => {
      return (
        <PdpSelectableCard
          alignment='center'
          fulfillmentType={ tile.fulfillmentType }
          selectAction={ tile.selectAction }
          updateVariantAction={ tile.updateVariantAction }
          icon={ tile.icon }
          key={ tile.fulfillmentType }
          onChange={ onChange }
          selected={ tile.selected }
          subtitle={ tile.subtitle }
          tileState={ tile.tileState }
          title={ tile.title }
        >
        </PdpSelectableCard>
      );
    } )
  );
};

/**
 * Return a `AddtoBag` button with favourties button next to it.
 *
 * @param {Object} props - The props passed to the component.
 * @param {Object} props.bookmarkAction - The action to add a bookmark .
 * @param {Object} props.removeBookmarkAction - The action to remove a bookmark.
 * @param {boolean} props.bookmarked - Indicates if the item is bookmarked.
 * @param {string} props.commerceId - The commerce ID.
 * @param {string} props.productId - The product ID.
 * @param {number} props.quantity - The quantity of the item to add.
 * @param {string} props.skuId - The SKU ID.
 * @param {Object} props.selectedFulfillment - the selected fulfillment option.
 * @param {boolean} props.isSecondary - if the button should appear as a secondary button.
 * @param {boolean} props.isDisabled - if the button should be disabled.
 * @returns {JSX.Element} returns the `FulfillmentButton` component.
 */
export const AddToBag = ( props ) => {
  const {
    bookmarkAction,
    invokeMutation,
    removeBookmarkAction,
    bookmarked,
    commerceId,
    productId,
    quantity,
    skuId,
    selectedFulfillment,
    isSecondary,
    inBagLabel,
    incrementBagAccessibilityLabel,
    successLabel,
    isDisabled,
    invokeAction,
    isSubscribed,
    subscriptionFrequency,
    updateSuccessLabel,
    showUpdateBag,
    setIsFulfillmentChanged,
    isWebStickyATBButton,
    stickyAddToBagAccessibilityLabel
  } = props;
  return (
    <FulfillmentButton
      isWebStickyATBButton={ isWebStickyATBButton }
      stickyAddToBagAccessibilityLabel={ stickyAddToBagAccessibilityLabel }
      invokeMutation={ invokeMutation }
      bookmarkAction={ bookmarkAction }
      bookmarked={ bookmarked }
      commerceId={ commerceId }
      productId={ productId }
      quantity={ quantity }
      inBagLabel={ inBagLabel }
      skuId={ skuId }
      isSecondaryState={ isSecondary }
      isDisabled={ isDisabled }
      incrementBagAccessibilityLabel={ incrementBagAccessibilityLabel }
      removeBookmarkAction={ removeBookmarkAction }
      successLabel={ successLabel }
      isSubscribed={ isSubscribed }
      subscriptionFrequency={ subscriptionFrequency }
      { ...spreadProps( { ...selectedFulfillment } ) }
      invokeAction={ invokeAction }
      updateSuccessLabel={ updateSuccessLabel }
      showUpdateBag={ showUpdateBag }
      setIsFulfillmentChanged={ setIsFulfillmentChanged }
    />
  );
};

/**
 * Returns a button inside a div for ViewItemsInOffer.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.viewItemsInOfferAction - The action for the button.
 * @param {Object} props.fulfillmentSection - details about the fulfillment section.
 * @returns {JSX.Element} returns a 'ViewItemsInOffer' component.
 */
export const ViewItemsInOffer = ( props ) => {
  const {
    viewItemsInOfferAction,
    isSaleableGwp
  } = props;
  const viewItemsButton = (
    <div className='viewItemsinOffer' >
      <Link_Huge className='viewItemsinOfferButton'
        likeButtonOutline={ isSaleableGwp }
        likeButtonSecondary={ !isSaleableGwp }
        action={ viewItemsInOfferAction }
      >
        { viewItemsInOfferAction?.label }
      </Link_Huge>
    </div>
  );
  return viewItemsButton;
};

/**
 * Returns a `ProductPreSale` component with perk and bookmark details.
 *
 * @param {Object} props - The properties to the component.
 * @param {Object} props.perk - The perk details to display.
 * @param {boolean} props.bookmarked - if the perk is bookmarked.
 * @param {Object} props.bookmarkAction - action to add a bookmark.
 * @param {Object} props.removeBookmarkAction - action to remove a bookmark.
 * @returns {JSX.Element} return the `ProductPreSale` .
 */
export const PlatinumPerk = ( props ) => {
  const {
    perk,
    bookmarked,
    bookmarkAction,
    removeBookmarkAction
  } = props;
  return (
    <ProductPreSale
      { ...spreadProps( { ...perk } ) }
      bookmarked={ bookmarked }
      bookmarkAction={ bookmarkAction }
      removeBookmarkAction={ removeBookmarkAction }
      { ...spreadProps( { ...props } ) }
    />
  );
};

/**
 * A wrapper component for rendering the `ProductComingSoon` component.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.comingSoonLabel - The label text to display for the "coming soon" section.
 * @param {string} props.comingSoonMessage - The message to display in the "coming soon" section.
 * @param {boolean} props.bookmarked - Indicates whether the item is currently bookmarked.
 * @param {Object} props.bookmarkAction - action to handle bookmarking the item.
 * @param {Object} props.removeBookmarkAction - action to handle removing the bookmark.
 * @returns {JSX.Element} The `ProductComingSoon` component
 */
export const ComingSoon = ( props ) => {
  const {
    comingSoonLabel,
    comingSoonMessage,
    bookmarked,
    bookmarkAction,
    removeBookmarkAction
  } = props;

  return (
    <ProductComingSoon
      comingSoonLabel={ comingSoonLabel }
      comingSoonMessage={ comingSoonMessage }
      bookmarked={ bookmarked }
      bookmarkAction={ bookmarkAction }
      removeBookmarkAction={ removeBookmarkAction }
      { ...spreadProps( { ...props } ) }
    />
  );
};

/**
 * A wrapper component for rendering the `InStoreOnlyItem` component.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.message - The label text to display for the "Available for In - Store Purchase only" section.
 * @param {boolean} props.bookmarked - Indicates whether the item is currently bookmarked.
 * @param {Object} props.bookmarkAction - action to handle bookmarking the item.
 * @param {Object} props.removeBookmarkAction - action to handle removing the bookmark.
 */
export const InStoreOnlyItem = ( props ) => {
  const { message } = props;
  if( !message ){
    return;
  }
  return (
    <div className='InStoreOnlyItem'>
      <Tag
        variant='utility'
        style='notification'
        message={ message }
      />
      <AsyncComponent
        id={ props.id }
        bookmarkAction={ props.bookmarkAction }
        bookmarked={ props.bookmarked }
        commerceId={ props.commerceId }
        invokeMutation={ props.invokeMutation }
        key='ProductBookmark'
        moduleName='ProductBookmark'
        productActionsProps={ props }
        productId={ props.productId }
        quantity={ props.quantity }
        removeBookmarkAction={ props.removeBookmarkAction }
        skuId={ props.skuId }
        state={ props.state }
      />
    </div>

  );
};

/**
 * Gets the selected fulfillment option from a list based on the given fulfillment type.
 *
 * @param {Array<Object>} arr - The list of fulfillment options.
 * @param {string} type - The fulfillment type
 * @returns {Object} The selected fulfillment option.
 */
export const getSelectedFulfillment = ( arr, type ) => {
  // early return
  if( !hasItems( arr ) ){
    return;
  }
  let foundFulfillment;
  if( type ){
    // if type is passed return fulfillmentType based on type or return default if not found
    foundFulfillment = arr.filter( ( item ) => item.fulfillmentType === type )[0];
  }
  else {
    // otherwise return the selected fulfillmentType
    foundFulfillment = arr.filter( ( item ) => item.selected )[0];
  }
  // return the found fulfillment or default
  return foundFulfillment || getSelectedFulfillment( arr, fulfillmentTypes.SHIP );
};

/**
 * Updates the state to the selected fulfillment type in the fulfillment options.
 *
 * @param {Object} state - The current state.
 * @param {Object} action - action to reducer function to update new state.
 * @returns {Object} The updated state with the selected fulfillment type.
 */
export const fulfillmentStateReducer = ( state, action ) => {
  const { type = fulfillmentTypes.SHIP } = action || {};
  switch ( type ){
    case STATE.UPDATE:
      const { cart = {}, store = {}, sddStore = {}, sddInfo = {}, isFulfillmentChanged } = action || {};
      const { cartBopisStoreId, cartSddStoreId, cartSddInfo } = cart;
      const { setIsSubscribed, setSubscriptionFrequency, setPrevLabel, setOriginalModuleState, setShowUpdateBag } = action.methods;
      const lastUpdateSnapshot = utils.getSelectedFulfillment( action.fulfillmentSection?.fulfillmentTileOptions );
      if( lastUpdateSnapshot ){
        const isFulfillmentTypeShip = lastUpdateSnapshot.fulfillmentType === fulfillmentTypes.SHIP;
        const isBopisStoreUnChanged = !isFulfillmentChanged && lastUpdateSnapshot.fulfillmentType === fulfillmentTypes.BOPIS && cartBopisStoreId === store?.bopisStoreId;
        const isSddStoreInfoUnChanged = !isFulfillmentChanged && lastUpdateSnapshot.fulfillmentType === fulfillmentTypes.SDD && cartSddStoreId === sddStore?.sddStoreId && isSddInfoMatches( cartSddInfo?.address, sddInfo?.sddInfo?.address );
        if( isFulfillmentTypeShip || isBopisStoreUnChanged || isSddStoreInfoUnChanged ){
          setIsSubscribed( lastUpdateSnapshot.subscription?.subsAndSaveOptIn?.value );
          setSubscriptionFrequency( lastUpdateSnapshot.subscription?.frequencyOptions?.value );
          setPrevLabel( lastUpdateSnapshot.buttonAction?.label );
          updateOriginalModuleState( { type: lastUpdateSnapshot?.fulfillmentType,
            isSubscribed: lastUpdateSnapshot?.subscription?.subsAndSaveOptIn?.value,
            subscriptionFrequency: lastUpdateSnapshot?.subscription?.frequencyOptions?.value
          }, { setOriginalModuleState } );
        }
        else {
          if( !isFulfillmentChanged && lastUpdateSnapshot?.fulfillmentType !== fulfillmentTypes.SHIP ){
            updateOriginalModuleState( { type: lastUpdateSnapshot?.fulfillmentType, isSubscribed: false, subscriptionFrequency: '' },
              { setOriginalModuleState } );
          }
          setShowUpdateBag( true );
        }
      }
      return action.payload; // Update the state with the new props
    case STATE.SUBSCRIPTION_TOGGLE:
      return {
        ...state,
        fulfillmentSection: {
          ...state.fulfillmentSection,
          fulfillmentTileOptions: state.fulfillmentSection?.fulfillmentTileOptions?.map( ( item ) => {
            if( item.subscription?.subsAndSaveOptIn?.disabled === false ){
              return {
                ...item,
                buttonAction: {
                  ...item.buttonAction,
                  label: action.payload.isSubscriptionOpted ? item.subscription?.addToBagSubscriptionLabel : action.payload.prevLabel
                }
              };
            }
            return item;
          } )
        }
      };
    case STATE.SUBSCRIPTION_FREQ_CHANGE:
      return {
        ...state,
        fulfillmentSection: {
          ...state.fulfillmentSection,
          fulfillmentTileOptions: state.fulfillmentSection?.fulfillmentTileOptions?.map( ( item ) => {
            if( item.subscription?.subsAndSaveOptIn?.disabled === false ){
              return {
                ...item,
                subscription: {
                  ...item.subscription,
                  frequencyOptions: {
                    ...item.subscription?.frequencyOptions,
                    value: action.payload.subscriptionFrequency
                  }
                }
              };
            }
            return item;
          } )
        }
      };
    default:
      return {
        ...state,
        fulfillmentSection:
        {
          ...state.fulfillmentSection,
          fulfillmentTileOptions: state.fulfillmentSection?.fulfillmentTileOptions?.map( ( item ) => {
            return {
              ...item,
              selected: item.fulfillmentType === type
            };
          } )
        }
      };
  }
};

/**
   * Function to determine which components should render.
   *
   * @param {object} data - object containing data for the components
   * @param {object} method - object containing helper functions (e.g. hasItems)
   * @returns {object} object with computed boolean values for each component
   */
export const getRenderComponents = ( data, method ) => {
  const {
    comingSoon,
    displayState,
    inStore,
    messages = [],
    notifyMe,
    PDP_ACTIONS_DISPLAY_STATE,
    perk,
    selectedFulfillment,
    showUpdateBag,
    deliveryInfo,
    removeSubscriptionMessage
  } = data;
  const { hasItems } = method;

  // Helper function to determine if "comingSoon" should be true
  const isComingSoon = () => {
    return !!( comingSoon && displayState === PDP_ACTIONS_DISPLAY_STATE.COMING_SOON );
  };

  // Helper function to determine if "fulfillmentTiles" should be true
  const isFulfillmentTiles = () => {
    return !!( displayState === PDP_ACTIONS_DISPLAY_STATE.FULFILLMENT_TILE );
  };

  // Helper function to determine if "inStore" should be true
  const isInStore = () => {
    return !!( inStore && hasItems( Object.keys( inStore ) ) );
  };

  // Helper function to determine if "notifyMe" should be true
  const isNotifyMe = () => {
    return !!( notifyMe?.notifyMeAction && selectedFulfillment.eligibilityState === PDP_ACTIONS_DISPLAY_STATE.NOTIFY_ME );
  };

  // Helper function to determine if "perk" should be true
  const isPerk = () => {
    return !!( perk && perk?.perkAction && displayState === PDP_ACTIONS_DISPLAY_STATE.PERK );
  };

  const showAddToBag = () => {
    return !!( selectedFulfillment.buttonAction || selectedFulfillment.addToBagAction || selectedFulfillment.updateBagAction );
  };

  // Helper function to combine messages
  const showInlineMessages = () => {
    if( !( messages?.length > 0 ) && !( selectedFulfillment.messages?.length > 0 ) ){
      return false;
    }
    else if( selectedFulfillment.messages?.length > 0 ){
      return [...( selectedFulfillment?.messages || [] ), ...( messages || [] )];
    }
    else {
      return messages;
    }
  };
  // Helper function to determine if "subscriptionMessages" should be true
  const showSubscriptionMessages = ()=> {
    return !!( showUpdateBag && removeSubscriptionMessage && selectedFulfillment.updateBagAction && selectedFulfillment.subscription?.subsAndSaveOptIn?.disabled );
  };

  const showAlternateStore = () => {
    return !! ( deliveryInfo?.alternateStoreTag?.message );
  };

  // Return the components object with computed values
  return {
    addToBag: showAddToBag(),
    comingSoon: isComingSoon(),
    fulfillmentTiles: isFulfillmentTiles(),
    inlineMessages: showInlineMessages(),
    inStore: isInStore(),
    notifyMe: isNotifyMe(),
    perk: isPerk(),
    subscriptionMessages: showSubscriptionMessages(),
    showAlternateStoreMessage: showAlternateStore()
  };
};

/**
 * Updates the original module state with the passed data
 *
 * @param {object} data - Data to be saved in original module state
 * @param {string} methods - Method to be called to save to the data to original module state
 */
export const updateOriginalModuleState = ( data, methods ) => {
  const { setOriginalModuleState } = methods;
  setOriginalModuleState( data );
};

/**
 * Wraps a component in a StickyContainer if isSticky is true, otherwise returns the component as is.
 *
 * @param {boolean} isSticky - Determines whether to wrap the component in a StickyContainer.
 * @param {ReactNode} component - The React component to potentially wrap.
 * @returns {ReactNode} The wrapped component or the original component.
 */
export const renderWithStickyContainer = ( props ) => {
  const { isLargeDevice, isWebStickyATBButton, component } = props;
  if( !isLargeDevice && isWebStickyATBButton ){
    return (
      <StickyContainer position='bottom'
        fullWidth='true'
        customClassName='ProductActionsV2__AddToBagSticky'
      >
        { component }
      </StickyContainer>
    );
  }
  return component;
};

/**
 * Checks if the given eligibility state is in the list of secondary states.
 *
 * @param {Array<string>} secondaryEligibilityStates - A list of secondary states.
 * @param {string} eligibilityState - The state to check.
 * @returns {boolean} `true` if the state is a secondary state, otherwise `false`.
 */
export const isSecondaryState = ( { secondaryEligibilityStates = utils.secondaryEligibilityStates, eligibilityState } ) => {
  return secondaryEligibilityStates.some( eligibility => eligibility === eligibilityState );
};

/**
 * Checks if the Cart Sdd Info Matches the updated Sdd Info
 *
 * @param {object} cartAddress - A list of states that are disabled.
 * @param {object} updatedAddress - The state to check.
 * @returns {boolean} `true` if the Cart Sdd Info Matches the updated Sdd Info, otherwise `false`.
 */
export const isSddInfoMatches = ( cartAddress = {}, updatedAddress = {} ) => {
  return ( cartAddress?.address1 === updatedAddress?.address1 &&
    cartAddress?.address2 === updatedAddress?.address2 &&
    cartAddress?.city === updatedAddress?.city &&
    cartAddress?.zipCode === updatedAddress?.zipCode &&
    cartAddress?.state === updatedAddress?.state
  );
};

/**
 * Checks if the given eligibility state is in the list of disabled states.
 *
 * @param {Array<string>} disabledEligibilityStates - A list of states that are disabled.
 * @param {string} eligibilityState - The state to check.
 * @returns {boolean} `true` if the state is disabled, otherwise `false`.
 */
export const isDisabledState = ( { disabledEligibilityStates = utils.disabledEligibilityStates, eligibilityState } ) => {
  return disabledEligibilityStates.some( eligibility => eligibility === eligibilityState );
};


/**
 * Defines the different types of fulfillment options.
 *
 * @type {Object}
 * @property {string} SHIP - "SHIP" fulfillment type.
 * @property {string} BOPIS - "BOPIS" fulfillment type.
 * @property {string} SDD - "SDD" fulfillment type.
 */
export const fulfillmentTypes = {
  SHIP: 'SHP',
  BOPIS: 'PICK',
  SDD: 'SDD'
};

/**
 * Defines the different types of states for reducer.
 *
 * @type {Object}
 * @property {string} UPDATE - "update the props".
 */
export const STATE = {
  UPDATE: 'UPDATE',
  SUBSCRIPTION_TOGGLE: 'SUBSCRIPTION_TOGGLE',
  SUBSCRIPTION_FREQ_CHANGE: 'SUBSCRIPTION_FREQ_CHANGE'
};

/**
 * Defines the different types of states for displayState.
 *
 * @type {Object}
 * @property {string} COMING_SOON - "Coming Soon" display type
 * @property {string} FULFILLMENT_TILE - "Fulfilment tile" display type (ship, pickup, same-day, etc)
 * @property {string} PERK - "Perk" (for rewards member) display type
 */
export const PDP_ACTIONS_DISPLAY_STATE = {
  COMING_SOON: 'COMING_SOON',
  FULFILLMENT_TILE: 'FULFILLMENT_TILE',
  NOTIFY_ME: 'notify_me',
  PERK: 'PERK'
};

/**
 * subscriptionChangeHandler function for subscription onclick event
 * @method
 * @param {object} data Arguments
 * @param {object} methods Methods
 * @param {object} eventData Event Data
 */
export const subscriptionChangeHandler = ( data, method ) => ( eventData ) => {
  const { prevLabel, selectedFulfillment, originalModuleState, subscriptionFrequency } = data;
  const { setIsSubscribed, setSubscriptionFrequency, setShowUpdateBag, dispatch } = method;
  setIsSubscribed( eventData );
  // when user moves from pick to ship the subscriptionFrequency is not set to its default value
  if( eventData === true && !subscriptionFrequency ){
    setSubscriptionFrequency( selectedFulfillment.subscription?.frequencyOptions?.value );
  }
  utils.setUpdateBagButtonStatus( { fulfillmentType: selectedFulfillment?.fulfillmentType, isSubsOpted: eventData, subsFrequency: selectedFulfillment?.subscription?.frequencyOptions?.value, originalModuleState, value: true },
    { setShowUpdateBag }
  );
  dispatch( {
    type: utils.STATE.SUBSCRIPTION_TOGGLE,
    payload: {
      isSubscriptionOpted: eventData,
      prevLabel: prevLabel
    }
  } );
};

/**
 * frequencyChangeHandler function for subscription frequency dropdown change event
 * @method
 * @param {object} methods Methods
 * @param {object} eventData Event Data
 */
export const frequencyChangeHandler = ( data, method ) => ( eventData ) => {
  const { selectedFulfillment, originalModuleState } = data;
  const { setSubscriptionFrequency, setShowUpdateBag, dispatch } = method;
  setSubscriptionFrequency( eventData );
  utils.setUpdateBagButtonStatus( { fulfillmentType: selectedFulfillment?.fulfillmentType, isSubsOpted: selectedFulfillment?.subscription?.subsAndSaveOptIn?.value, subsFrequency: eventData, originalModuleState, value: true },
    { setShowUpdateBag }
  );
  dispatch( {
    type: utils.STATE.SUBSCRIPTION_FREQ_CHANGE,
    payload: {
      subscriptionFrequency: eventData
    }
  } );
};

/**
 * fulfillmentChangeHandler function for fulfillment type change event
 * @method
 * @param {object} methods Methods
 * @param {object} state current state
 * @param {object} action Change requested
 */
export const fulfillmentChangeHandler = ( data, method ) => ( state, action ) => {
  const { selectAction = {}, updateVariantAction } = state;
  const { dispatch, invokeAction, setShowUpdateBag, setIsFulfillmentChanged } = method;
  const { skuId, productId, isSubscribed, subscriptionFrequency, originalModuleState, fulfillmentOptions, cart, store, sddStore, sddInfo } = data;
  const { dataCaptureData } = selectAction;
  if( !dispatch ){
    return;
  }
  dispatch( state, action );
  const selectedFulfillment = utils.getSelectedFulfillment( fulfillmentOptions, state.type );
  const isSubscriptionSelected = selectedFulfillment?.subscription?.subsAndSaveOptIn?.disabled ? false : isSubscribed;
  utils.setUpdateBagButtonStatus( { fulfillmentType: state.type, isSubsOpted: isSubscriptionSelected, subsFrequency: subscriptionFrequency, originalModuleState, cart, store, sddStore, sddInfo, value: true },
    { setShowUpdateBag, setIsFulfillmentChanged }
  );
  if( updateVariantAction ){
    invokeAction( {
      ...updateVariantAction,
      variables: {
        moduleParams: { productId, skuId }
      }
    } );
  }

  if( dataCaptureData ){
    datacapture?.processEvents(
      { dataCapture: { ...dataCaptureData } },
      constants.DATACAPTURE_EVENT_TYPE.click
    );
  }
};

/**
 * onModuleUpdate function for reloading all component on update event
 * @method
 * @param {object} data Arguments
 * @param {object} methods Methods
 * @param {object} eventData Event Data
 */
export const onModuleUpdate = ( data, method ) => {
  const { fulfillmentSection, props, cart, store, sddStore, sddInfo, isFulfillmentChanged } = data;
  const { setIsSubscribed, setSubscriptionFrequency, setShowUpdateBag, setPrevLabel, setOriginalModuleState, dispatch } = method;
  utils.setUpdateBagButtonStatus( { value: false }, { setShowUpdateBag } );

  // Only update if fulfillmentSection have changed
  dispatch( { type: utils.STATE.UPDATE, payload: props, fulfillmentSection, cart, store, sddStore, sddInfo, isFulfillmentChanged, methods: { setIsSubscribed, setSubscriptionFrequency, setPrevLabel, setOriginalModuleState, setShowUpdateBag } } );
};

/**
 * setUpdateBagButtonStatus function for updating the update button state
 * @method
 * @param {object} data Arguments
 * @param {object} methods Methods
 */
export const setUpdateBagButtonStatus = ( data, methods ) => {
  const { fulfillmentType, isSubsOpted, subsFrequency, originalModuleState, cart, store, sddStore, sddInfo, value } = data;
  const { setShowUpdateBag, setIsFulfillmentChanged } = methods;
  const { cartBopisStoreId, cartSddStoreId, cartSddInfo } = cart || {};
  const isFulfillmentTypeChanged = fulfillmentType !== originalModuleState?.type;
  const isSubscriptionOptInChanged = isSubsOpted !== originalModuleState?.isSubscribed;
  const isSubscriptionFrequencyChanged = isSubsOpted && subsFrequency !== originalModuleState?.subscriptionFrequency;
  const isBopisStoreChanged = fulfillmentType === fulfillmentTypes.BOPIS && cartBopisStoreId !== store?.bopisStoreId;
  const isSddStoreInfoChanged = fulfillmentType === fulfillmentTypes.SDD && cartSddStoreId !== sddStore?.sddStoreId && !isSddInfoMatches( cartSddInfo?.address, sddInfo?.sddInfo?.address );
  if( value && setIsFulfillmentChanged ){
    setIsFulfillmentChanged( isFulfillmentTypeChanged );
  }

  if( value &&
    ( isFulfillmentTypeChanged || isSubscriptionOptInChanged || isSubscriptionFrequencyChanged || isBopisStoreChanged || isSddStoreInfoChanged ) ){
    setShowUpdateBag( true );
  }
  else {
    setShowUpdateBag( false );
  }
};

export default ProductActionsV2;
